export enum WhatsappTemplateCategory {
  message = "message",
  tasks = "task",
}

export enum WhatsappTemplateStatus {
  approved = "APPROVED",
  pending = "PENDING",
  rejected = "REJECTED",
  deleted = "DELETED",
}

export interface AIAgentInsights {
  ai_replies: number;
  human_sent_messages: number;
  missed_ai_reply: number;
  resolved: number;
  total_ai_conversations: number;
  total_conversations: number;
  unresolved: number;
}

export interface CRMUnreadCounts {
  chatbot_unread: number;
  ig_unread: number;
  twilio_unread: number;
  wa_unread: number;
  // optional until the backend ships email unread counts
  email_unread?: number;
}

export interface WhatsappTemplateValues {
  name: string;
  board_id?: string;
  action?: string;
  header: string;
  bodyPlain: string;
  bodyHtml: string;
  footer: string;
}

export type WhatsappTemplatePayload = Pick<
  WhatsappTemplateValues,
  "name" | "board_id" | "action"
> & {
  type: WhatsappTemplateCategory;
  template: {
    components: {
      text: string;
      type: string;
      format?: string;
      example?: {
        body_text: string[][];
      };
    }[];
  };
};

export enum WhatsappAgentLogStatus {
  sent = "sent",
  failed = "failed",
  payment_error = "payment_error",
}

export enum CRMConversationFilter {
  active = "active",
  expired = "expired",
  blocked = "blocked",
  closed = "closed",
}

export enum CRMConversationStatus {
  active = "active",
  closed = "closed",
}

export enum CRMChannel {
  whatsapp = "whatsapp",
  instagram = "instagram",
  chatbot = "chatbot",
  twilio = "twilio",
  email = "email",
}

export enum CRMConversationMode {
  allChats = "all_chats",
  onAIAutoReply = "auto_reply",
  waitingResponse = "waiting_response",
  managedByMe = "managed_be_me",
}

export type WhatsappQualityRating = "GREEN" | "YELLOW" | "RED" | "UNKNOWN";

export type WhatsappBusinessVerificationStatus =
  | "expired"
  | "failed"
  | "ineligible"
  | "not_verified"
  | "pending"
  | "pending_need_more_info"
  | "pending_submission"
  | "rejected"
  | "revoked"
  | "verified";

export type WhatsappProfile = {
  description?: string;
  email?: string;
  profile_picture_url?: string;
  websites?: string[];
};

export enum InstagramMessageType {
  text = "text",
  image = "image",
  video = "video",
  audio = "audio",
}

export type SendWhatsappMessageMutationParams = {
  entityId: string;
  data: {
    to: string;
    conv_name: string;
    context?: {
      from: string;
      id: string;
    };
  } & (
    | {
        type: "text";
        payload: {
          body: string;
        };
      }
    | {
        type: "image";
        payload: {
          id: string;
          caption: string;
        };
      }
    | {
        type: "audio";
        payload: {
          id: string;
        };
      }
    | {
        type: "video";
        payload: {
          id: string;
          caption: string;
        };
      }
    | {
        type: "document";
        payload: {
          id: string;
          caption: string;
          filename: string;
        };
      }
    | {
        name: string;
      }
  );
};

export type SendInstagramMessageMutationParams = {
  entityId: string;
  data:
    | {
        payload: {
          timestamp: number;
          recipient: {
            id: string;
          };
          message: {
            text: string;
          };
        };
      }
    | {
        msg_type: "attachment";
        payload: {
          timestamp: number;
          recipient: {
            id: string;
          };
          message: {
            attachments: {
              type: Exclude<InstagramMessageType, "text">;
              media_id: string;
              payload: {
                url: string;
              };
            }[];
          };
        };
      };
};

export type SendTwilioMessageMutationParams = {
  entityId: string;
  data: {
    to: string;
    body: string;
  };
};

export type SendEmailMessageMutationParams = {
  entityId: string;
  data: {
    message_id: string;
    // conv_id of the conversation the message belongs to
    name: string;
    // message body in HTML format
    text: string;
    files?: File[];
  };
};

export enum CRMConversationAutoResponseStatus {
  inactive = 0,
  active = 1,
}

export enum CRMConversationAutoReplyStatus {
  inactive = 0,
  active = 1,
  handedOver = 2,
}

export type ConnectionType = "shopify" | "calendly" | "squareup" | "cal";

export type ConnectionStatus =
  | "connected"
  | "requires_configuration"
  | "expired";

export interface CalendlyEventType {
  duration: number;
  id: string;
  name: string;
}

export interface CalcomEventType {
  id: number;
  name: string;
  duration: number;
  booking_url: string;
  scheduleId: number | null;
}

export interface SquareupLocation {
  id: string;
  name: string;
}

export interface ConnectionPropsBase {
  status: ConnectionStatus;
  next_steps: string[];
  available_actions: string[];
  available_resources: string[];
}

export type ConnectionPropsCalendly = ConnectionPropsBase & {
  event_type?: CalendlyEventType;
};

export type ConnectionPropsSquareup = ConnectionPropsBase & {
  location?: SquareupLocation;
};

export type ConnectionProps =
  | ConnectionPropsBase
  | ConnectionPropsCalendly
  | ConnectionPropsSquareup
  | Record<string, never>;

export interface ConnectionDto {
  id: string;
  name: string;
  type: ConnectionType;
  props: ConnectionProps;
}

// Agent Config

export type AgentToolPack =
  | "generic"
  | "shopify"
  | "calendly"
  | "squareup"
  | "cal"
  | "custom"
  | "contact";

export type ContactPackFieldType = "text" | "email" | "phone";

export interface ContactPackField {
  title: string;
  type: ContactPackFieldType;
  required: boolean;
  variable_name: string;
}

export interface ContactPackParams {
  fields: ContactPackField[];
}

export interface CustomTemplateVariable {
  name: string;
  desc: string;
}

export interface CustomTemplateData {
  enabled: boolean;
  name?: string;
  description?: string;
  method: string;
  url: string;
  headers: Record<string, string>;
  body: Record<string, unknown>;
  variables: CustomTemplateVariable[];
}

export interface AgentTool {
  pack: AgentToolPack;
  tools?: string[] | null;
  params?: Record<string, unknown>;
  connection_id?: string;
  custom_template_data?: CustomTemplateData[];
}

export type AgentTone = "business" | "neutral" | "creative";
export type AgentEmojiUsage = "none" | "average" | "many";
export type AgentInstructionOptionId =
  | "custom-prompt"
  | "customer-support-agent"
  | "service-agent"
  | "sales-agent";

export interface AgentRateLimiter {
  limit: number;
  enabled: boolean;
  period_ms: number;
  msg: string;
}

export interface AgentConfigData {
  business_name: string;
  instructions: string;
  when_to_collect_lead: string;
  selected_instruction_option: AgentInstructionOptionId;
  saved_instructions: { name: AgentInstructionOptionId; value: string }[];
  tools: AgentTool[];
  tone: AgentTone;
  emoji_usage: AgentEmojiUsage;
  response_length: number;
  when_to_handover: string;
  rate_limiter?: AgentRateLimiter;
}

export interface AgentConfigDto {
  id: string;
  agent_id: string;
  config: AgentConfigData;
  updated_at: string;
}
